import React, { useState } from 'react';
import { truncateText } from '../../utils/helpers';

/**
 * UserPresence component - Single user badge with avatar and tooltip
 * @param {Object} user - User object with userId, displayName, cursorColor, etc.
 * @param {boolean} showTooltip - Whether to show tooltip on hover (default: true)
 * @param {string} size - Size variant: 'sm', 'md', 'lg' (default: 'md')
 * @param {boolean} showName - Whether to show display name (default: true)
 * @param {boolean} isUsingAI - Whether user is currently using AI (default: false)
 */
function UserPresence({ 
  user, 
  showTooltip = true, 
  size = 'md', 
  showName = true,
  isUsingAI = false
}) {
  const [isHovered, setIsHovered] = useState(false);

  // Size configurations
  const sizeConfig = {
    sm: {
      avatar: 'w-6 h-6 text-xs',
      container: 'gap-1.5',
      text: 'text-xs'
    },
    md: {
      avatar: 'w-8 h-8 text-sm',
      container: 'gap-2',
      text: 'text-sm'
    },
    lg: {
      avatar: 'w-10 h-10 text-base',
      container: 'gap-2.5',
      text: 'text-base'
    }
  };

  const config = sizeConfig[size] || sizeConfig.md;

  // Get user's initials (up to 2 characters)
  const getInitials = (name) => {
    if (!name || typeof name !== 'string') return '?';
    
    const words = name.trim().split(' ');
    if (words.length === 1) {
      return words[0].substring(0, 2).toUpperCase();
    }
    return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
  };

  const initials = getInitials(user.displayName);
  const displayName = truncateText(user.displayName, 20);

  return (
    <div className="relative">
      {/* User badge */}
      <div 
        className={`flex items-center ${config.container} cursor-pointer hover:bg-gray-50 rounded-lg p-1 transition-colors duration-150`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Avatar with user's color and initials */}
        <div className="relative">
          <div
            className={`${config.avatar} rounded-full flex items-center justify-center font-bold text-white shadow-sm border border-white/20`}
            style={{ backgroundColor: user.cursorColor }}
          >
            {initials}
          </div>
          
          {/* Online status indicator */}
          <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
        </div>

        {/* Display name */}
        {showName && (
          <div className="flex-1 min-w-0">
            <p className={`${config.text} font-medium text-gray-700 truncate`}>
              {displayName}
            </p>
            {user.lastSeen && (
              <p className="text-xs text-gray-500">
                Active now
              </p>
            )}
          </div>
        )}

        {/* Color dot or AI badge */}
        {isUsingAI ? (
          <div className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white px-2 py-0.5 rounded text-xs font-bold flex items-center gap-1 animate-pulse">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path d="M13 7H7v6h6V7z" />
              <path fillRule="evenodd" d="M7 2a1 1 0 012 0v1h2V2a1 1 0 112 0v1h2a2 2 0 012 2v2h1a1 1 0 110 2h-1v2h1a1 1 0 110 2h-1v2a2 2 0 01-2 2h-2v1a1 1 0 11-2 0v-1H9v1a1 1 0 11-2 0v-1H5a2 2 0 01-2-2v-2H2a1 1 0 110-2h1V9H2a1 1 0 010-2h1V5a2 2 0 012-2h2V2zM5 5h10v10H5V5z" clipRule="evenodd" />
            </svg>
            AI
          </div>
        ) : (
          <div
            className="w-3 h-3 rounded-full border border-gray-300"
            style={{ backgroundColor: user.cursorColor }}
          ></div>
        )}
      </div>

      {/* Tooltip */}
      {showTooltip && isHovered && (
        <div className="absolute z-50 left-1/2 transform -translate-x-1/2 bottom-full mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-lg whitespace-nowrap max-w-xs">
          <div className="font-medium">{user.displayName}</div>
          {user.joinedAt && (
            <div className="text-xs text-gray-300">
              Joined {new Date(user.joinedAt).toLocaleTimeString()}
            </div>
          )}
          
          {/* AI Activity Indicator in Tooltip */}
          {isUsingAI && user.currentAIPrompt && (
            <div className="mt-2 pt-2 border-t border-gray-700">
              <div className="flex items-center gap-1 text-xs text-purple-300 mb-1">
                <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span className="font-medium">Asking AI:</span>
              </div>
              <div className="text-xs text-gray-300 italic whitespace-normal break-words">
                "{truncateText(user.currentAIPrompt, 50)}"
              </div>
            </div>
          )}
          
          {/* Tooltip arrow */}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );
}

/**
 * Avatar-only variant for compact displays
 */
export function UserAvatar({ user, size = 'md', showTooltip = true }) {
  return (
    <UserPresence
      user={user}
      showName={false}
      size={size}
      showTooltip={showTooltip}
    />
  );
}

/**
 * Cursor indicator variant - shows cursor position
 */
export function CursorPresence({ user }) {
  const [isVisible, setIsVisible] = useState(true);

  // Auto-hide after period of inactivity
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
    }, 3000); // Hide after 3 seconds of no movement

    return () => clearTimeout(timer);
  }, [user.cursorX, user.cursorY]);

  if (!isVisible) return null;

  return (
    <div 
      className="absolute pointer-events-none z-50 transition-all duration-200"
      style={{
        left: user.cursorX + 10,
        top: user.cursorY - 25,
      }}
    >
      <div 
        className="px-2 py-1 rounded-md text-xs font-medium text-white shadow-lg"
        style={{ backgroundColor: user.cursorColor }}
      >
        {user.displayName}
      </div>
      
      {/* Cursor pointer */}
      <div 
        className="absolute w-2 h-2 rounded-full -bottom-1 -left-1"
        style={{ backgroundColor: user.cursorColor }}
      ></div>
    </div>
  );
}

export default UserPresence;
